
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { SearchIcon, SparklesIcon } from '@heroicons/react/outline';
import { StarIcon } from '@heroicons/react/solid';
import Fuse from 'fuse.js';
import { debounce } from 'lodash';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { useFavoriteTools } from '@/hooks/useFavoriteTools';
import Filter from '@/components/atoms/Filter';
import FilterItem from '@/components/atoms/FilterItem';
import YoutubeModal from '@/components/atoms/YoutubeModal';
import DashboardLayout from '@/components/layout/DashboardLayout';
import ButtonLink from '@/components/links/ButtonLink';
import PresetItem from '@/components/presets/PresetItem';
import Seo from '@/components/Seo';
import { Preset, PresetCategory, presetHasCategory, presets, visiblePresetCategories, } from '@/constants/presets';
import { useAnalytics } from '@/contexts/Analytics';
import axiosInstance from '@/utils/httpClient';
import { useUser } from '@/utils/useUser';
const Presets = () => {
    const { user } = useUser();
    const { favoriteTools } = useFavoriteTools();
    const [selectedCategory, setCategory] = useState<PresetCategory | null>(null);
    const [showFavorites, setShowFavorites] = useState<boolean>(false);
    const [showMyTools, setShowMyTools] = useState<boolean>(false);
    const [search, setSearch] = useState<string | null>(null);
    const [sort, setSort] = useState<string>('popular');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [userPresets, setUserPresets] = useState<Preset[]>([]);
    const { t } = useLocale('tools');
    const analytics = useAnalytics();
    useEffect(() => {
        if (userPresets.length > 0 || !user?.id)
            return;
        const fetchPresets = async () => {
            try {
                // Own saved templates + templates shared with the user's team
                const response = await axiosInstance.get('/api/templates/list');
                const templates = response.data?.templates || [];
                setUserPresets(templates.map((template: {
                    preset: Preset;
                    sharedByTeam?: boolean;
                }) => ({
                    ...template.preset,
                    icon: SparklesIcon,
                    iconForeground: 'text-primary-700',
                    iconBackground: 'bg-primary-50',
                    category: 'Other',
                    isCustom: true,
                    ...(template.sharedByTeam ? { badge: 'Team' } : {}),
                })));
            }
            catch (error) {
                console.log(error);
            }
        };
        fetchPresets();
    }, [user?.id]);
    const debouncedSearch = useMemo(() => debounce((value: string) => {
        if (!value) {
            return;
        }
        analytics.track('Tools Searched', {
            searchValue: value,
            selectedCategory,
        });
    }, 1500), []);
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSearch(value);
    };
    // Create memoized Fuse instance
    const fuse = useMemo(() => {
        const options = {
            keys: ['title', 'translatedTitle', 'searchKeywords'],
            threshold: 0.3,
            includeScore: true,
            minMatchCharLength: 2,
            shouldSort: true,
            weightedKeys: [
                { name: 'title', weight: 2 },
                { name: 'translatedTitle', weight: 2 },
                { name: 'searchKeywords', weight: 1 },
            ],
        };
        // Create dataset with translated titles for search
        const searchablePresets = [...userPresets, ...presets]
            .filter((preset) => !preset.hideDashoard)
            .map((preset) => ({
            ...preset,
            translatedTitle: t(preset.title),
        }));
        return new Fuse(searchablePresets, options);
    }, [userPresets, t]); // Depend on userPresets and t since they're loaded asynchronously
    // filtered and sorted presets
    const filteredPresets = useMemo(() => {
        let results = [...userPresets, ...presets].filter((preset) => !preset.hideDashoard);
        // Apply favorites filter
        if (showFavorites) {
            results = results.filter((preset) => favoriteTools.includes(preset.slug));
        }
        // Apply "My Tools" filter — user-created dynamic templates only
        else if (showMyTools) {
            results = results.filter((preset) => preset.isCustom);
        }
        // Apply category filter
        else if (selectedCategory) {
            results = results.filter((preset) => presetHasCategory(preset, selectedCategory));
        }
        // Apply search filter using Fuse.js if search is present
        if (search) {
            const searchResults = fuse.search(search);
            results = searchResults.map((result) => result.item);
        }
        // Apply sorting
        results = results.sort((a, b) => {
            if (sort === 'alphabetical') {
                return t(a.title).localeCompare(t(b.title));
            }
            if (sort === 'newest') {
                return (new Date(b.dateAdded || '2023').getTime() -
                    new Date(a.dateAdded || '2023').getTime());
            }
            // Apply favorites sorting with recency
            const aIndex = favoriteTools.indexOf(a.slug);
            const bIndex = favoriteTools.indexOf(b.slug);
            const aIsFavorite = aIndex !== -1;
            const bIsFavorite = bIndex !== -1;
            // Both favorited - sort by recency (higher index = more recent = should come first)
            if (aIsFavorite && bIsFavorite) {
                return bIndex - aIndex;
            }
            // Only a is favorited
            if (aIsFavorite && !bIsFavorite) {
                return -1;
            }
            // Only b is favorited
            if (!aIsFavorite && bIsFavorite) {
                return 1;
            }
            // Neither favorited - maintain original order
            return 0;
        });
        return results;
    }, [
        search,
        selectedCategory,
        showFavorites,
        showMyTools,
        sort,
        favoriteTools,
        userPresets,
        fuse,
        t,
    ]);
    const handleCategoryChange = (category: PresetCategory | null) => {
        setCategory(category);
        setShowFavorites(false);
        setShowMyTools(false);
    };
    const handleFavoritesToggle = () => {
        setShowFavorites(true);
        setShowMyTools(false);
        setCategory(null);
    };
    const handleMyToolsToggle = () => {
        setShowMyTools(true);
        setShowFavorites(false);
        setCategory(null);
    };
    return (<DashboardLayout>
      <main className='flex-1 overflow-y-auto focus:outline-none'>
        {showModal && (<YoutubeModal showModal={showModal} youtubeLink='https://www.youtube.com/embed/b2M_0H4O9gY?autoplay=1&mute=1&showinfo=0&rel=0' onClose={() => setShowModal(false)}/>)}
        <Seo templateTitle={t('Tools')}/>
        <div className='relative mx-auto max-w-5xl xl:px-4 md:px-8 2xl:max-w-6xl'>
          <div className='pb-16 pt-10'>
            <div className='flex items-center justify-between px-4 sm:px-6 md:px-0'>
              <h1 className='text-3xl font-extrabold text-gray-900 dark:text-slate-200'>
                {t('Tools')} [{filteredPresets.length}]
              </h1>
              <div className='flex items-center gap-2'>
                {/* <Button
          variant='ghost'
          onClick={() => setShowModal(true)}
          className='flex items-center text-sm text-gray-500 hover:text-gray-700  dark:text-slate-500 dark:hover:text-slate-300'
        >
          <PlayIcon
            className='mr-1.5 h-5 w-5 flex-shrink-0 '
            aria-hidden='true'
          />
          {t('How to use')}
        </Button> */}
                {/* Magic AI tool — generate a custom tool from a prompt */}
                <ButtonLink href='/presets/dynamic' variant='outline' className='text-sm'>
                  <SparklesIcon className='mr-1.5 h-5 w-5 flex-shrink-0' aria-hidden='true'/>
                  {t('Create Tool')}
                </ButtonLink>
              </div>
            </div>
            <div className='min-w-0 flex-1 md:px-8 lg:px-0 xl:col-span-6'>
              <div className='flex items-center px-6 py-4 md:mx-auto md:max-w-3xl lg:mx-0 lg:max-w-none xl:px-0'>
                <div className='w-full'>
                  <label htmlFor='search' className='sr-only'>
                    {t('Search')}
                  </label>
                  <div className='relative'>
                    <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                      <SearchIcon className='h-5 w-5 text-gray-400' aria-hidden='true'/>
                    </div>
                    <input id='search' name='search' className='block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:border-primary-500 focus:text-gray-900 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-0 dark:bg-slate-700/50 dark:text-white sm:text-sm' placeholder={t('Search')} type='search' value={search as string} onChange={handleSearchChange}/>
                  </div>
                </div>
              </div>
            </div>
            <div className='px-4 sm:px-6 md:px-0'>
              <div className='flex flex-col justify-between sm:flex-row'>
                <Filter>
                  <FilterItem onClick={() => handleCategoryChange(null)} isActive={selectedCategory === null &&
            !showFavorites &&
            !showMyTools}>
                    {t('All')}
                  </FilterItem>
                  {/* Favorite Presets */}
                  {favoriteTools.length > 0 && (<FilterItem isActive={showFavorites} onClick={handleFavoritesToggle}>
                      <span className='flex items-center gap-1.5'>
                        <StarIcon className='h-4 w-4 text-yellow-400'/>
                        {t('Favorites')}
                      </span>
                    </FilterItem>)}
                  {/* User-created dynamic templates (Magic AI tools) */}
                  {userPresets.length > 0 && (<FilterItem isActive={showMyTools} onClick={handleMyToolsToggle}>
                      <span className='flex items-center gap-1.5'>
                        <SparklesIcon className='h-4 w-4'/>
                        {t('My Tools')}
                      </span>
                    </FilterItem>)}
                  {visiblePresetCategories.map((category) => (<FilterItem key={category} isActive={selectedCategory === category} onClick={() => handleCategoryChange(category)}>
                      {t(category)}
                    </FilterItem>))}
                </Filter>
                {/* sorting */}
                <div className='ml-10 hidden items-start justify-between py-4 lg:flex'>
                  <div className='flex items-center'>
                    <div className='flex sm:items-center'>
                      {/* <label
          htmlFor='sort-by'
          className='block text-sm font-medium text-gray-700 dark:text-slate-400'
        >
          {t('Sort by')}
        </label> */}
                      <select id='sort-by' name='sort-by' className='ml-3 block rounded-md border-transparent py-2 pr-7 text-base font-medium focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 sm:text-sm' value={sort} onChange={(e) => setSort(e.target.value)}>
                        <option value='featured'>{t('Featured')}</option>
                        <option value='alphabetical'>
                          {t('Alphabetical')}
                        </option>
                        <option value='newest'>{t('Newest')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              {filteredPresets.length === 0 ? (<div className='flex items-center justify-center py-12'>
                  <p className='text-lg text-gray-500'>
                    {t('No templates found for this category or search. Please try a different search.')}
                  </p>
                </div>) : (<div className='mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'>
                  {filteredPresets.map((preset, presetIdx) => (<PresetItem key={preset.slug + presetIdx} preset={preset} presetIdx={presetIdx} presetsLength={presets.length}/>))}
                </div>)}
            </div>
          </div>
        </div>
      </main>
    </DashboardLayout>);
};
export default Presets;
// export const getServerSideProps = withPageAuth({ redirectTo: '/login' });

    async function __Next_Translate__getStaticProps__1a12e86ec9a__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/presets/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86ec9a__ as getStaticProps }
  